<template>
  <div
    class="drop-zone"
    :class="[containerClass, { 'drag-active': isDragging }]"
    @dragover.prevent
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onContainerDrop"
  >
    <div
      v-for="(item, index) in items"
      class="draggable-item"
      :class="{
        dragging: draggedIndex === index,
        'drag-over': dragOverIndex === index,
      }"
      :key="getItemKey(item)"
      :draggable="isDraggable(item)"
      @dragstart="isDraggable(item) ? onDragStart($event, item, index) : null"
      @dragend="onDragEnd"
      @dragover.prevent="isDraggable(item) ? onDragOver($event, index) : null"
      @dragenter.prevent="isDraggable(item) ? onDragEnter($event, index) : null"
      @dragleave.prevent="isDraggable(item) ? onDragLeave($event, index) : null"
      @drop.prevent="isDraggable(item) ? onDrop($event, index) : null"
    >
      <slot name="item" :item="item" :index="index"></slot>
    </div>

    <slot name="empty" v-if="!items || items.length === 0">
      <div class="empty-state">
        <p>No items available</p>
      </div>
    </slot>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  // Array of items to be dragged
  items: {
    type: Array,
    required: true,
    default: () => [],
  },
  // Function to determine if an item is draggable
  isDraggableItem: {
    type: Function,
    default: () => true,
  },
  // Function to get a unique key for each item
  itemKey: {
    type: [String, Function],
    default: 'id',
  },
  // CSS class for the container
  containerClass: {
    type: [String, Object, Array],
    default: 'grid grid-cols-1 gap-1 w-11/12 max-w-3xl mx-auto mt-4',
  },
});

const emit = defineEmits(['drag-start', 'drag-over', 'drag-end', 'reorder']);

// Track dragging state
const isDragging = ref(false);
const draggedIndex = ref(null);
const dragOverIndex = ref(null);
const dragCounter = ref(0);

const getItemKey = item => {
  if (typeof props.itemKey === 'function') {
    return props.itemKey(item);
  }
  return item[props.itemKey];
};

const isDraggable = item => {
  return props.isDraggableItem(item);
};

const onDragStart = (event, item, index) => {
  isDragging.value = true;
  draggedIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
  // Store the index instead of the ID for more flexibility
  event.dataTransfer.setData('text/plain', index);

  // Add a small delay to set the ghost image
  setTimeout(() => {
    if (event.target) {
      event.target.classList.add('dragging');
    }
  }, 0);

  emit('drag-start', { event, item, index });
};

const onDragEnd = event => {
  isDragging.value = false;
  draggedIndex.value = null;
  dragOverIndex.value = null;
  dragCounter.value = 0;

  if (event.target) {
    event.target.classList.remove('dragging');
  }

  emit('drag-end', { event });
};

const onDragOver = (event, index) => {
  event.dataTransfer.dropEffect = 'move';
  dragOverIndex.value = index;
  emit('drag-over', { event, index });
};

const onDragEnter = (event, index) => {
  if (index !== undefined) {
    dragOverIndex.value = index;
  } else {
    // Container drag enter
    dragCounter.value++;
  }
};

const onDragLeave = (event, index) => {
  if (index !== undefined) {
    if (dragOverIndex.value === index) {
      dragOverIndex.value = null;
    }
  } else {
    // Container drag leave
    dragCounter.value--;
    if (dragCounter.value <= 0) {
      dragCounter.value = 0;
      isDragging.value = false;
    }
  }
};

const onContainerDrop = event => {
  isDragging.value = false;
  dragCounter.value = 0;
};

const onDrop = (event, dropIndex) => {
  const draggedItemIndex = parseInt(event.dataTransfer.getData('text/plain'), 10);

  if (isNaN(draggedItemIndex) || draggedItemIndex === dropIndex) {
    isDragging.value = false;
    draggedIndex.value = null;
    dragOverIndex.value = null;
    return;
  }

  const targetElement = event.currentTarget;
  const bounding = targetElement.getBoundingClientRect();
  const offset = event.clientY - bounding.top;
  const midpoint = bounding.height / 2;

  // Determine if the item should be placed before or after the target
  let newIndex = dropIndex;
  if (offset > midpoint) newIndex += 1;
  if (draggedItemIndex < newIndex) newIndex--;

  emit('reorder', {
    oldIndex: draggedItemIndex,
    newIndex: newIndex,
    item: props.items[draggedItemIndex],
  });

  // Reset drag states
  isDragging.value = false;
  draggedIndex.value = null;
  dragOverIndex.value = null;
};
</script>

<style scoped>
.drop-zone {
  padding: 0.5rem;
  min-height: 50px;
  border-radius: 0.25rem;
  transition: background-color 0.2s ease;
}

.drop-zone.drag-active {
  background-color: rgba(59, 130, 246, 0.05);
  border: 1px dashed rgba(59, 130, 246, 0.5);
}

.draggable-item {
  cursor: grab;
  transition: all 0.2s ease;
  position: relative;
  user-select: none;
  border-radius: 0.25rem;
}

.draggable-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.draggable-item:active {
  cursor: grabbing;
  background-color: rgba(0, 0, 0, 0.08);
  transform: scale(0.98);
}

.draggable-item.dragging {
  opacity: 0.5;
  background-color: rgba(59, 130, 246, 0.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.draggable-item.drag-over {
  border-top: 2px solid #3b82f6;
}

.draggable-item[draggable='false'] {
  cursor: default;
  opacity: 0.7;
}

.draggable-item[draggable='false']:hover {
  transform: none;
  box-shadow: none;
  background-color: transparent;
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: #666;
  border: 1px dashed #ccc;
  border-radius: 0.25rem;
  margin: 1rem 0;
}
</style>
